import "core-js/modules/es6.array.slice";
import _includes from "lodash/includes";
import _keys from "lodash/keys";
import _map from "lodash/map";
import _some from "lodash/some";
import _get from "lodash/get";

var _createReducer;

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { createReducer } from 'redux-act';
import nwApiSDK from '@nerdwallet/nw-api-sdk';
import { signin, authenticateWithGoogle, authenticateWithCorpIdp, authenticateWithApple, register, signout, resetPassword, updatePassword, createPassword, setSecurityQuestion, createTUCustomer, deactivateTUCustomer, sendPasswordResetEmail, verifyEmail, updateProfileFail, unsubscribe, unsubscribeV2, sendSmsVerification, sendSmsReverification, verifySmsCode, attemptProviderMFA, createBillsharkAccount, getSavingsEstimate, refreshProviderAccount, updateFinancialAccountVisibility, createTransactionTag, deleteTransactionTag, createTransaction, deleteTransaction, editTransaction, createMerchantCategoryRule, changeBillCategory, createBills, updateBill, deleteBill, setBillPaidDate } from "../../actions";
import { createMoneyManagementUser, cancelMoneyManagementTransfer } from "../../features/money-management/actions";
import { AppleAuthErrors } from "../../constants/internal";
var UNSUBSCRIBE_FAIL_MESSAGE = 'Unable to unsubscribe. Manage your email communications from your Account Settings page.';

var getBillValidationError = function getBillValidationError(payload) {
  var error = payload.data.error;

  var nameErrorObjects = _get(error, 'data.fields.identity_id, name');

  var isNameErrorType = _some(nameErrorObjects, function (errorObject) {
    return _get(errorObject, 'type') === 'unique';
  });

  if (error.type === 'InvalidInputError' && isNameErrorType) {
    return 'Hm, looks like you repeated a bill name. Try again';
  }

  return 'Hm, some wires were crossed. Try again.';
};

var getSmsVerificationError = function getSmsVerificationError(resp) {
  var internalMessage = _get(resp, 'data.error.data.internal_message');

  var innerErrorType = _get(resp, 'data.error.data.inner_error.type');

  if (internalMessage === 'phone number associated with identity is already verified') {
    return 'This phone number is already verified.';
  }

  if (internalMessage === 'too many send_sms_verification requests since previous request') {
    return 'Please wait a few seconds before requesting a new code.';
  }

  if (internalMessage === 'phone number already used to verify an account') {
    return 'This phone number has already been used for SMS verification.';
  }

  if (internalMessage === 'Not a valid phone number') {
    return 'This is not a valid US phone number. Please enter a mobile number supplied by a US phone carrier. Online phone numbers will not work.';
  }

  if (innerErrorType === 'TwilioRestException') {
    return 'An error occurred while sending your verification code. Please check that you have a valid phone number.';
  }

  return 'An error occurred while processing your request.';
};

export default createReducer((_createReducer = {}, _defineProperty(_createReducer, register.fail, function (state, payload, _ref) {
  var resp = _ref.resp;

  if (_get(resp, 'resp.statusCode') === 404 || _get(resp, 'data.error.data.fields.email[0].type') === 'unique') {
    return 'An account with that email address already exists. Please try signing in.';
  }

  return 'There was an error with your registration. Please try registering again or contact support.';
}), _defineProperty(_createReducer, signin.fail, function (state, payload, _ref2) {
  var resp = _ref2.resp;

  var type = _get(resp, 'data.error.type');

  if (type === 'NotFoundError') return 'Invalid email address or password.';

  if (type === 'InvalidInputError') {
    var fields = _get(resp, 'data.error.data.fields');

    if (fields) {
      return "Missing or invalid input: ".concat(_map(_keys(fields), function (key) {
        return key.charAt(0).toUpperCase() + key.slice(1);
      }).join(', '));
    }

    return 'Invalid input.';
  }

  if (type === 'TooManyRequestsError') {
    return 'Too many failed login attempts. Please try again later or click on the forgot password link below.';
  }

  return "We're having trouble logging you in. Please try again later.";
}), _defineProperty(_createReducer, authenticateWithGoogle.fail, function (state, payload, _ref3) {
  var authType = _ref3.authType;
  // 'popup_closed_by_user' comes from google api's `authInstance.signIn()` on web.
  // Don't show an error message in this case.
  // https://developers.google.com/identity/sign-in/web/reference#googleauthsigninoptions
  if (_get(payload, 'error') === 'popup_closed_by_user') return;

  if (_get(payload, 'provider_id_conflict')) {
    return 'A NerdWallet account already exists for this Google email address.';
  }

  return "Failed to ".concat(authType === 'signin' ? 'sign in' : 'register', " with your Google account.");
}), _defineProperty(_createReducer, authenticateWithCorpIdp.fail, function (state, payload) {
  return payload;
}), _defineProperty(_createReducer, authenticateWithApple.fail, function (state, payload, _ref4) {
  var authType = _ref4.authType,
      usingPassword = _ref4.usingPassword;

  if (_includes([AppleAuthErrors.POPUP_CLOSED_BY_USER, AppleAuthErrors.CROSS_ORIGIN_POST_MESSAGE, // Ignore STATE_PARAM_MISMATCH because it's likely just the result of a user clicking "Sign in with Apple" multiple times
  AppleAuthErrors.STATE_PARAM_MISMATCH], payload) || _get(payload, 'data.error.data.user_must_set_password')) {
    return;
  }

  if (_get(payload, 'data.error.data.requires_password')) {
    // Only show an error if user has tried entering their NW password
    if (usingPassword) {
      return 'Invalid password';
    }

    return;
  }

  if (_get(payload, 'data.error.data.provider_id_conflict')) {
    return 'A NerdWallet account already exists for this Apple ID.';
  }

  return "Failed to ".concat(authType === 'signin' ? 'sign in' : 'register', " with your Apple account.");
}), _defineProperty(_createReducer, signout.fail, function (state, payload, _ref5) {
  var resp = _ref5.resp;
  return resp.err;
}), _defineProperty(_createReducer, resetPassword.fail, function (state, payload, _ref6) {
  var resp = _ref6.resp;
  var message = 'An error has occurred.';

  if (_get(resp, 'data.error.type') === 'AuthenticationError') {
    message = 'Incorrect answer to security question.';
  }

  return message;
}), _defineProperty(_createReducer, updatePassword.fail, function (state, payload, _ref7) {
  var resp = _ref7.resp;
  // default message
  var message = '';

  if (_get(resp, 'data.error.data.internal_message') === 'invalid old_password') {
    message = 'Please check your old password and try again.';
  } else if (_get(resp, 'data.error.data.fields.password[0].type') === 'new_value') {
    message = 'New password must be different from old password.';
  } else message = 'Failed to update password.';

  return message;
}), _defineProperty(_createReducer, createPassword.fail, function () {
  return 'Failed to create your password.';
}), _defineProperty(_createReducer, setSecurityQuestion.fail, function () {
  return 'Error saving security questions.';
}), _defineProperty(_createReducer, createTUCustomer.fail, function (state, payload) {
  var internalMessage = payload.internal_message; // NW account already has an active TU profile

  if (internalMessage === 'credit profile already active for user') {
    return 'We already have this credit account on record.';
  } // User tried to activate a TU profile that is already active for a *different* NW account


  if (internalMessage === 'Active TU Enrollment already exists for SSN') {
    return 'Hey, old friend! We have your credit score but it’s connected to a different email of yours. Please sign in with that account.';
  }

  if (internalMessage === 'failed Schematics input validation') {
    return 'An invalid input was detected in your request. Please refresh the page and verify all your information is correct.';
  }

  if (internalMessage === 'User has attempted authentication too many times') {
    return 'Too many unsuccessful verification attempts to connect to your account. Please try again in 30 days.';
  } // when a user is marked fraudulent and requires sms verification we don't want them to know that they've been marked
  // silencing the error message gives the illusion of a regular flow


  if (internalMessage === 'User requires SMS verification') {
    return;
  }

  return 'We are unable to find your information at this point.';
}), _defineProperty(_createReducer, deactivateTUCustomer.fail, function () {
  return 'Failed to remove account.';
}), _defineProperty(_createReducer, sendPasswordResetEmail.fail, function (state, payload, _ref8) {
  var resp = _ref8.resp;
  return resp.err;
}), _defineProperty(_createReducer, verifyEmail.fail, function (state, payload, _ref9) {
  var resp = _ref9.resp;
  return resp.err;
}), _defineProperty(_createReducer, updateProfileFail, function (state, payload, meta) {
  var showMessages = meta.showMessages,
      updating = meta.updating;
  if (!showMessages) return;

  if (updating === 'name') {
    return 'Failed to update name.';
  }

  if (updating === 'subscriptions') {
    return 'Failed to update notification preferences.';
  }

  if (updating === 'creditPurpose') {
    return 'Failed to update credit score purpose.';
  }

  if (updating === 'financial_profile' || updating === 'onboarding_profile') {
    return 'Failed to update onboarding profile.';
  }

  return 'Failed to update profile.';
}), _defineProperty(_createReducer, unsubscribe.fail, function () {
  return UNSUBSCRIBE_FAIL_MESSAGE;
}), _defineProperty(_createReducer, unsubscribeV2.fail, function () {
  return UNSUBSCRIBE_FAIL_MESSAGE;
}), _defineProperty(_createReducer, sendSmsVerification.fail, function (state, payload, _ref10) {
  var resp = _ref10.resp;
  return getSmsVerificationError(resp);
}), _defineProperty(_createReducer, sendSmsReverification.fail, function (state, payload, _ref11) {
  var resp = _ref11.resp;
  return getSmsVerificationError(resp);
}), _defineProperty(_createReducer, verifySmsCode.fail, function (state, payload, _ref12) {
  var resp = _ref12.resp;

  var tokenApiError = _get(resp, 'data.error.data.token_api_error');

  var errorType = _get(resp, 'data.error.type');

  if (tokenApiError === 'TOKEN_NOT_FOUND') {
    return "Hm. That's not the right code — try again.";
  }

  if (tokenApiError === 'EXPIRED_TOKEN') {
    return 'Hm. That code is expired. Click "Send another code" to get a new one.';
  }

  if (errorType === 'TooManyRequestsError') {
    return 'Hm. You\'ve entered an incorrect code too many times. Wait 3 minutes, then click "Send another code" to get a new one.';
  }

  return 'Sorry, there was an issue on our end. It could be a few different things. Try clicking "Send another code," or wait and try again later.';
}), _defineProperty(_createReducer, attemptProviderMFA.fail, function () {
  return 'Something went wrong verifying your account. Please try again later.';
}), _defineProperty(_createReducer, createBillsharkAccount.fail, function (state, payload, _ref13) {
  var resp = _ref13.resp;

  var type = _get(resp, 'data.error.type');

  if (type === 'AuthenticationError') {
    return 'An error occurred while authenticating your account.';
  }

  if (type === 'NotFoundError') {
    return 'There was a problem finding that biller.';
  }

  if (type === 'InvalidInputError') {
    var userMessage = _get(resp, 'data.error.data.user_message');

    var internalMessage = _get(resp, 'data.error.data.internal_message');

    var specificError = userMessage || internalMessage;
    return "There was a problem with your input: ".concat(specificError);
  }

  if (type === 'InternalError') {
    return 'Something went wrong while submitting your request. Please try again later.';
  }

  if (type === 'ServiceUnavailableError') {
    return 'Something went wrong while submitting your request. Please try again later.';
  }

  if (type === 'DuplicationError') {
    return "Looks like you've already registered for Billshark with this email. Please try again.";
  }

  return 'An error occurred while processing your request.';
}), _defineProperty(_createReducer, getSavingsEstimate.fail, function (state, payload, _ref14) {
  var resp = _ref14.resp;

  var type = _get(resp, 'data.error.type');

  if (type === 'AuthenticationError') {
    return 'An error occurred while authenticating your account.';
  }

  if (type === 'NotFoundError') {
    return 'There was a problem finding that biller.';
  }

  if (type === 'InvalidInputError') {
    return 'There was a problem with your input.';
  }

  if (type === 'InternalError') {
    return 'Something went wrong while submitting your request. Please try again later.';
  }

  if (type === 'ServiceUnavailableError') {
    return 'Something went wrong while submitting your request. Please try again later.';
  }

  return 'An error occurred while processing your request.';
}), _defineProperty(_createReducer, refreshProviderAccount.fail, function (state, payload, _ref15) {
  var _resp$data, _resp$data$error, _payload$graphQLError, _payload$graphQLError2;

  var resp = _ref15.resp;
  var type = resp === null || resp === void 0 ? void 0 : (_resp$data = resp.data) === null || _resp$data === void 0 ? void 0 : (_resp$data$error = _resp$data.error) === null || _resp$data$error === void 0 ? void 0 : _resp$data$error.type;
  var graphqlError = payload === null || payload === void 0 ? void 0 : (_payload$graphQLError = payload.graphQLErrors) === null || _payload$graphQLError === void 0 ? void 0 : (_payload$graphQLError2 = _payload$graphQLError[0]) === null || _payload$graphQLError2 === void 0 ? void 0 : _payload$graphQLError2.message;

  if (type === 'TooManyRequestsError' || graphqlError === 'error requesting data from source: TooManyRequestsError' || graphqlError === 'provider account state already refreshed recently') {
    return 'Hang on there. You can refresh each account once every 15 minutes.';
  }

  return 'An error occurred while refreshing your account. Try again later.';
}), _defineProperty(_createReducer, updateFinancialAccountVisibility.fail, function () {
  return 'Something went wrong while hiding your account. Please try again later.';
}), _defineProperty(_createReducer, createTransactionTag.fail, function (state, payload, meta) {
  var tag = _get(meta, 'tag');

  var resp = _get(meta, 'resp');

  var type = _get(resp, 'data.error.type');

  if (type === 'InternalError') {
    if (tag === nwApiSDK.IDENTITY_TRANSACTION_TAGS.NOT_RECURRING) {
      return 'We ran into a problem processing your request. Please try again later.';
    }

    if (tag === nwApiSDK.IDENTITY_TRANSACTION_TAGS.IGNORED) {
      return 'Something went wrong while hiding your transaction. Please try again later.';
    }

    return 'We ran into a problem processing your request. Please try again later.';
  }

  return 'We ran into a problem processing your request. Please try again later.';
}), _defineProperty(_createReducer, deleteTransactionTag.fail, function (state, payload, meta) {
  var tag = _get(meta, 'tag');

  var resp = _get(meta, 'resp');

  var type = _get(resp, 'data.error.type');

  if (type === 'NotFoundError') {
    if (tag === nwApiSDK.IDENTITY_TRANSACTION_TAGS.NOT_RECURRING) {
      return 'We ran into a problem processing your request. Please try again later.';
    }

    return 'We ran into a problem processing your request. Please try again later.';
  }

  return 'We ran into a problem processing your request. Please try again later.';
}), _defineProperty(_createReducer, createTransaction.fail, function () {
  return "Something went wrong with creating this transaction. Please try again later.";
}), _defineProperty(_createReducer, deleteTransaction.fail, function () {
  return "Something went wrong with deleting this transaction. Please try again later.";
}), _defineProperty(_createReducer, editTransaction.fail, function () {
  return "Something went wrong with editing this transaction. Please try again later.";
}), _defineProperty(_createReducer, createMerchantCategoryRule.fail, function (state, payload, meta) {
  var category = meta.category,
      canonicalMerchantName = meta.canonicalMerchantName;
  return "Something went wrong recategorizing your ".concat(canonicalMerchantName, " transactions to the ").concat(category, " category");
}), _defineProperty(_createReducer, changeBillCategory.fail, function () {
  return 'Something went wrong recategorizing your bill. Please try again later.';
}), _defineProperty(_createReducer, createBills.fail, function (state, payload) {
  return getBillValidationError(payload);
}), _defineProperty(_createReducer, updateBill.fail, function (state, payload) {
  return getBillValidationError(payload);
}), _defineProperty(_createReducer, deleteBill.fail, function () {
  return 'Something went wrong deleting your bill. Please try again later.';
}), _defineProperty(_createReducer, setBillPaidDate.fail, function () {
  return 'Something went wrong when trying to update your bill. Please try again later.';
}), _defineProperty(_createReducer, createMoneyManagementUser.fail, function () {
  return "Hm, we're having trouble getting you set up to transfer. Try again later.";
}), _defineProperty(_createReducer, cancelMoneyManagementTransfer.fail, function (state, payload, _ref16) {
  var resp = _ref16.resp;

  var errorType = _get(resp, 'data.error.type');

  if (errorType === 'TransferCancelNotPossible') {
    return "Sorry, the cancellation window just closed. This transfer can't be canceled.";
  }

  return "Sorry, we couldn't cancel your transfer. Please try again in a few minutes.";
}), _createReducer), null);